import { useEffect, useRef } from "react";
import { useSidebar } from "../context/SidebarContext";
import { Outlet, useLocation } from "react-router";
import * as Sentry from "@sentry/react";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import AppSidebar from "./AppSidebar";
import { useBrands } from "../hooks/useBrands";
import BillingPaymentMethodBanner from "../components/common/BillingPaymentMethodBanner";
import ShopifyAppUpdateBanner from "../components/common/ShopifyAppUpdateBanner";
import ErrorFallback from "../components/common/ErrorFallback";

// Shell renders sidebar + header once, never remounts when switching between layouts
export const AppShell: React.FC = () => {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const { pathname } = useLocation();
  const bannersRef = useRef<HTMLDivElement>(null);
  useBrands();

  useEffect(() => {
    const banners = bannersRef.current;
    if (!banners) return;
    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty("--app-banner-height", `${entry.contentRect.height}px`);
    });
    observer.observe(banners);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <div ref={bannersRef} className="sticky top-0 z-1100">
        <BillingPaymentMethodBanner />
        <ShopifyAppUpdateBanner />
      </div>
      <div className="min-h-[calc(100vh_-_var(--app-banner-height))] xl:flex">
        <div>
          <AppSidebar />
          <Backdrop />
        </div>
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${
            isExpanded || isHovered ? "lg:ml-[290px]" : "lg:ml-[90px]"
          } ${isMobileOpen ? "ml-0" : ""}`}
        >
          <AppHeader />
          <div className="p-4 md:p-6 bg-gray-50">
            <Sentry.ErrorBoundary fallback={<ErrorFallback />} key={pathname.split("/")[1]}>
              <Outlet />
            </Sentry.ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

// Default content wrapper — full width (no max-width cap)
const AppLayout: React.FC = () => <Outlet />;

// Wide content wrapper without max-width
export const AppLayoutWide: React.FC = () => <Outlet />;

export default AppLayout;
